"use client";

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuthenticatedFetch } from "@/hooks/useAuthenticatedFetch";
import { AddTickerModal } from "@/components/modals/AddTickerModal";
import { EditTickerModal } from "@/components/modals/EditTickerModal";
import { Timeframe } from "@/lib/enums/timeframe.enum";
import { useIndicatorRegistry } from "@/lib/registry/IndicatorRegistry";
import { registerIndicators } from "@/lib/indicators";

interface SystemStats {
  users: {
    total: number;
    active: number;
    admins: number;
    roots: number;
    owners: number;
  };
}

export function AdminPage() {
  const { isAdmin, loading, isSignedIn } = useUserRole();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingTickers, setLoadingTickers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [indicatorCount, setIndicatorCount] = useState<number>(0);
  const indicatorRegistry = useIndicatorRegistry();

  // Ticker management state
  const [isAddTickerVisible, setIsAddTickerVisible] = useState(false);
  const [isEditTickerVisible, setIsEditTickerVisible] = useState(false);
  const [allTickers, setAllTickers] = useState<{ symbol: string }[]>([]);
  const [exchanges, setExchanges] = useState<string[]>([]);
  const [timeframes, setTimeframes] = useState<string[]>([]);

  const normalizeTickers = (tickersData: unknown): { symbol: string }[] => {
    if (!Array.isArray(tickersData)) {
      return [];
    }
    return tickersData.flatMap((item) => {
      if (!item || typeof item !== "object") return [];
      const record = item as Record<string, unknown>;
      return typeof record.symbol === "string"
        ? [{ symbol: record.symbol }]
        : [];
    });
  };

  // Redirect if not admin
  useEffect(() => {
    if (!loading && !isSignedIn) {
      navigate("/", { replace: true });
    }
    if (!loading && isSignedIn && !isAdmin) {
      navigate("/", { replace: true });
    }
  }, [loading, isAdmin, isSignedIn, navigate]);

  // Register indicators and capture count for stats card
  useEffect(() => {
    registerIndicators();
    setIndicatorCount(indicatorRegistry.getAll().length);
  }, [indicatorRegistry]);

  // Fetch users and stats
  useEffect(() => {
    async function fetchStats() {
      if (!isAdmin) return;

      try {
        const statsData = await authenticatedFetch<SystemStats>("/admin/stats");
        setStats(statsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
      } finally {
        setLoadingData(false);
      }
    }

    if (isAdmin) {
      void fetchStats();
    }
  }, [isAdmin, authenticatedFetch]);

  useEffect(() => {
    // Use all available exchanges and timeframes, not just the ones in use
    // This allows admins to add new ticker/exchange/timeframe combinations
    const allExchanges = ["binance", "bybit", "kraken"];
    const allTimeframes = Object.values(Timeframe);
    setExchanges(allExchanges);
    setTimeframes(allTimeframes);
  }, []);

  const refreshTickers = React.useCallback(async () => {
    setLoadingTickers(true);
    try {
      const tickersData = await authenticatedFetch<unknown>("/ticker/get");
      setAllTickers(normalizeTickers(tickersData));
    } catch {
      setAllTickers([]);
    } finally {
      setLoadingTickers(false);
    }
  }, [authenticatedFetch]);

  const handleAddTicker = async (tickerData: {
    symbol: string;
    pair: string;
    exchange: string;
    candleSyncTimeframes: Timeframe[];
  }): Promise<boolean> => {
    try {
      // Send the data directly as expected by the backend
      await authenticatedFetch("/ticker/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          symbol: tickerData.symbol,
          pair: tickerData.pair,
          exchange: tickerData.exchange,
          candleSyncTimeframes: tickerData.candleSyncTimeframes,
        }),
      });

      await refreshTickers();
      return true;
    } catch {
      return false;
    }
  };

  const handleActivatePair = async (pairId: number): Promise<boolean> => {
    try {
      await authenticatedFetch(`/pair/activate/${pairId}`, { method: "POST" });
      await refreshTickers();
      return true;
    } catch {
      return false;
    }
  };

  const handleDeactivatePair = async (pairId: number): Promise<boolean> => {
    try {
      await authenticatedFetch(`/pair/deactivate/${pairId}`, {
        method: "POST",
      });
      await refreshTickers();
      return true;
    } catch {
      return false;
    }
  };

  const handleDeletePair = async (pairId: number): Promise<boolean> => {
    try {
      await authenticatedFetch(`/pair/delete/${pairId}`, { method: "DELETE" });
      await refreshTickers();
      return true;
    } catch {
      return false;
    }
  };

  const handleDeleteTicker = async (symbol: string): Promise<boolean> => {
    try {
      await authenticatedFetch(`/ticker/delete/${symbol}`, {
        method: "DELETE",
      });
      await refreshTickers();
      return true;
    } catch {
      return false;
    }
  };

  if (loading || loadingData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-950">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-950 p-4 sm:p-6 md:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <h1 className="text-3xl font-bold text-white">Admin</h1>
          <button
            onClick={() => navigate("/")}
            className="w-full rounded-md bg-gray-700 px-4 py-2 text-sm text-white hover:bg-gray-600 md:w-auto"
          >
            Back
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-900 p-4 text-red-200">
            {error}
          </div>
        )}

        {/* System Stats */}
        {stats && (
          <div className="mb-8 grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
              <div className="text-2xl font-bold text-white">
                {stats.users.total}
              </div>
              <div className="text-sm text-gray-400">Total Users</div>
            </div>
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
              <div className="text-2xl font-bold text-green-400">
                {stats.users.active}
              </div>
              <div className="text-sm text-gray-400">Active Users</div>
            </div>
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
              <div className="text-2xl font-bold text-teal-400">
                {indicatorCount}
              </div>
              <div className="text-sm text-gray-400">Indicators</div>
            </div>
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
              <div className="text-2xl font-bold text-blue-400">
                {stats.users.admins}
              </div>
              <div className="text-sm text-gray-400">Admins</div>
            </div>
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
              <div className="text-2xl font-bold text-purple-400">
                {stats.users.roots}
              </div>
              <div className="text-sm text-gray-400">Roots</div>
            </div>
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
              <div className="text-2xl font-bold text-yellow-400">
                {stats.users.owners}
              </div>
              <div className="text-sm text-gray-400">Owners</div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h2 className="mb-4 text-xl font-semibold text-white">
            Admin Actions
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <button
              onClick={() => {
                setIsEditTickerVisible(true);
                void refreshTickers();
              }}
              className="rounded-md border border-green-600 bg-green-600 px-4 py-3 text-white transition-colors hover:bg-green-700"
            >
              Manage Tickers
            </button>
            <button
              onClick={() => navigate("/admin/users")}
              className="rounded-md border border-gray-600 bg-gray-700 px-4 py-3 text-white transition-colors hover:bg-gray-600"
            >
              Manage Users
            </button>
            <button
              onClick={() => navigate("/admin/indicators")}
              className="rounded-md border border-blue-600 bg-blue-600 px-4 py-3 text-white transition-colors hover:bg-blue-700"
            >
              Indicator Settings
            </button>
          </div>
        </div>
      </div>

      {/* Ticker Modals */}
      <AddTickerModal
        isVisible={isAddTickerVisible}
        onClose={() => setIsAddTickerVisible(false)}
        onAdd={handleAddTicker}
        exchanges={exchanges}
        onDeletePair={handleDeletePair}
        onDeleteTicker={handleDeleteTicker}
        loadingTickers={loadingTickers}
        timeframes={timeframes?.map((t) => Timeframe[t as Timeframe])}
      />

      <EditTickerModal
        isVisible={isEditTickerVisible}
        onClose={() => setIsEditTickerVisible(false)}
        onActivate={handleActivatePair}
        onDeactivate={handleDeactivatePair}
        onOpenAddTicker={() => {
          setIsEditTickerVisible(false);
          setTimeout(() => setIsAddTickerVisible(true), 0);
        }}
        onAddPair={handleAddTicker}
        tickers={allTickers.map((t) => t.symbol)}
        timeframes={timeframes?.map((t) => Timeframe[t as Timeframe])}
        exchanges={exchanges}
        currentPair=""
      />
    </div>
  );
}
